import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '@pf/shared';
import {
  useClients,
  useCreateClient,
  useArchiveClient,
  useUnarchiveClient,
  type CreateClientBody,
} from '../hooks/useClients';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function clientInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

interface CreateClientModalProps {
  onClose: () => void;
  onSuccess: (id: string) => void;
}

function CreateClientModal({ onClose, onSuccess }: CreateClientModalProps) {
  const createClient = useCreateClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [contactEmailsRaw, setContactEmailsRaw] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const contactEmails = contactEmailsRaw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      const body: CreateClientBody = { name: name.trim() };
      if (description.trim()) body.description = description.trim();
      if (website.trim()) body.website = website.trim();
      if (logoUrl.trim()) body.logoUrl = logoUrl.trim();
      if (contactEmails.length > 0) body.contactEmails = contactEmails;
      const result = await createClient.mutateAsync(body);
      onSuccess((result as unknown as { client: Client }).client?.id ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Client</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Acme Corp"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Optional description..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/logo.png"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stakeholder Emails
              <span className="ml-1 font-normal text-gray-400 text-xs">(comma-separated)</span>
            </label>
            <input
              value={contactEmailsRaw}
              onChange={(e) => setContactEmailsRaw(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ceo@acme.com, cmo@acme.com"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createClient.isPending}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
            >
              {createClient.isPending ? 'Creating...' : 'Create client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface CardActionsProps {
  client: Client;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  busy: boolean;
}

function CardActions({ client, onArchive, onUnarchive, busy }: CardActionsProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1 text-gray-400 hover:text-gray-600 rounded"
        title="Actions"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          {client.archivedAt ? (
            <button
              onClick={() => { onUnarchive(client.id); setOpen(false); }}
              disabled={busy}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
            >
              Unarchive
            </button>
          ) : (
            <button
              onClick={() => { onArchive(client.id); setOpen(false); }}
              disabled={busy}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
            >
              Archive
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [actionId, setActionId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: activeData, isLoading: activeLoading, isError: activeError } = useClients({
    search: debouncedSearch || undefined,
  });
  const { data: archivedData } = useClients({
    search: debouncedSearch || undefined,
    archived: true,
  });

  const archiveMutation = useArchiveClient();
  const unarchiveMutation = useUnarchiveClient();

  const activeClients = (activeData as unknown as { clients: Client[] } | undefined)?.clients ?? [];
  const archivedClients = (archivedData as unknown as { clients: Client[] } | undefined)?.clients ?? [];
  const displayedClients = showArchived ? archivedClients : activeClients;
  const archivedCount = archivedClients.length;

  function handleCreated(id: string) {
    setShowCreate(false);
    if (id) navigate(`/clients/${id}`);
  }

  async function handleArchive(id: string) {
    setActionId(id);
    try {
      await archiveMutation.mutateAsync(id);
    } finally {
      setActionId(null);
    }
  }

  async function handleUnarchive(id: string) {
    setActionId(id);
    try {
      await unarchiveMutation.mutateAsync(id);
    } finally {
      setActionId(null);
    }
  }

  return (
    <div className="p-6">
      {showCreate && (
        <CreateClientModal onClose={() => setShowCreate(false)} onSuccess={handleCreated} />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Clients</h1>
          {displayedClients.length > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              {displayedClients.length} {showArchived ? 'archived' : 'active'} client
              {displayedClients.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived((v) => !v)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {showArchived ? 'Show active' : `Show archived (${archivedCount})`}
            </button>
          )}
          <button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
          >
            New Client
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-5">
        <input
          type="search"
          placeholder="Search clients by name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full max-w-sm border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {activeLoading && (
        <div className="text-center py-10 text-sm text-gray-500">Loading...</div>
      )}
      {activeError && (
        <div className="text-center py-10 text-sm text-red-600">Failed to load clients.</div>
      )}
      {!activeLoading && !activeError && displayedClients.length === 0 && (
        <div className="text-center py-16 text-sm text-gray-500">
          {debouncedSearch
            ? 'No clients match your search.'
            : showArchived
            ? 'No archived clients.'
            : 'No clients yet. Create your first one.'}
        </div>
      )}

      {!activeLoading && displayedClients.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayedClients.map((client) => (
            <div
              key={client.id}
              onClick={() => navigate(`/clients/${client.id}`)}
              className={`bg-white rounded-lg border border-gray-200 shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all flex flex-col gap-3 ${client.archivedAt ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  {client.logoUrl ? (
                    <img
                      src={client.logoUrl}
                      alt={client.name}
                      className="w-10 h-10 rounded-md object-cover flex-shrink-0 border border-gray-100"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-semibold leading-none">
                        {clientInitials(client.name)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{client.name}</p>
                    {client.website && (
                      <p className="text-xs text-gray-400 truncate">
                        {client.website.replace(/^https?:\/\//, '')}
                      </p>
                    )}
                  </div>
                </div>
                <CardActions
                  client={client}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  busy={actionId === client.id}
                />
              </div>

              {client.description && (
                <p className="text-xs text-gray-500 line-clamp-2">{client.description}</p>
              )}

              <div className="flex items-center gap-3 mt-auto pt-2 border-t border-gray-100">
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-900">
                    {client.pressListsCount ?? 0}
                  </p>
                  <p className="text-xs text-gray-400">press lists</p>
                </div>
                {client.archivedAt && (
                  <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                    archived
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
